import React from 'react';

import type { UpdateEntityFileSelectFunc, VideoComponentData } from './../ricos-content/index.js';
import type { MediaUploadFn } from './../_shared/create-media-upload-fn.js';
import type { PickMediaFn } from './../_shared/media/pick-media.js';
import type { DecodedInstance } from './../_shared/request-service-provider/parse-instance.js';
import type { FetchSitePages } from './../_shared/site-pages/types.js';
import type { AiRequestService } from './../plugin-ai/utils/use-ai-request-service.js';
import type { GifsResult } from './../plugin-giphy/types.js';
import type {
  EmbeddableItem,
  VerticalApiType,
} from './../plugin-vertical-embed/types.js';

/** Context to inject dev mocks */
export const DevContext = React.createContext<DevContextProps>({});

export type DevContextProps = {
  trackRender?: (componentName: string) => void;
  aiRequestService?: AiRequestService;
  giphyRequestService?: {
    getTrendingGifs: (params: {
      path: 'search' | 'trending';
      category: 'gifs' | 'stickers';
      limit: number;
      offset: number;
      searchTag?: string;
    }) => Promise<{ type: string; results: GifsResult }>;
    getSearchGifs: (params: {
      path: 'search' | 'trending';
      category: 'gifs' | 'stickers';
      limit: number;
      offset: number;
      searchTag?: string;
    }) => Promise<{ type: string; results: GifsResult }>;
  };
  pmNodeAttrs?: Record<string, any>;
  fetchSitePages?: FetchSitePages;
  fetchOembedWithAuthFromDevContext?: (url: string) => Promise<{
    html?: string;
    title?: string;
    thumbnail_url?: string;
    thumbnail_width?: number;
    thumbnail_height?: number;
  }>;
  fetchVerticalEmbedItemsFromDevContext?: (
    type: VerticalApiType,
    query?: string
  ) => Promise<EmbeddableItem[]>;
  fetchVerticalEmbedItemFromDevContext?: (
    type: VerticalApiType,
    embedId: string
  ) => Promise<EmbeddableItem>;
  renderCounts?: Map<string, number>;
  pickMedia?: PickMediaFn;
  resetRenderCounts?: () => void;
  onRenderCountChange?: (listener: (renderCounts: Map<string, number>) => void) => () => void;
  connectionState?: ConnectionState;
  setConnectionState?: React.Dispatch<React.SetStateAction<ConnectionState>>;
  logBi?: (params: { dataHook: string; value?: string | boolean | number }) => void;
  decodedInstance?: Partial<DecodedInstance>;
  handleFileUpload?: MediaUploadFn;
  handleFileSelection?: (
    updateEntity: UpdateEntityFileSelectFunc<any>,
    options?: { multiple?: boolean; index?: number; isCover?: boolean }
  ) => Promise<void>;
};

export type DevService = () => DevContextProps;

export const useDevService = () => {
  const service = React.useContext(DevContext);
  const serviceRef = React.useRef<DevContextProps>(service);
  serviceRef.current = service;
  return React.useCallback(() => serviceRef.current, []);
};

export type ConnectionState =
  | { type: 'BROKEN'; delay: number; status: number; response?: any }
  | { type: 'DELAYED'; delay: number }
  | { type: 'NO_MODIFICATION' };

export const useTrackRender = (componentName: string) =>
  React.useContext(DevContext)?.trackRender?.(componentName);

export const useDevGiphyRequestService = () => React.useContext(DevContext)?.giphyRequestService;
export const useDevAiRequestService = () => React.useContext(DevContext)?.aiRequestService;
export const useDevConnectionState = () =>
  (React.useContext(DevContext) as DevContextProps)?.connectionState ?? {
    type: 'NO_MODIFICATION',
  };

export const useDevLogBi = () => {
  return (React.useContext(DevContext) as DevContextProps)?.logBi ?? undefined;
};

export const useDevPmNodeAttrs = () => React.useContext(DevContext)?.pmNodeAttrs ?? undefined;

export const useDevPickMedia = () => React.useContext(DevContext)?.pickMedia ?? undefined;

export const useDevVerticalEmbedItems = () =>
  React.useContext(DevContext)?.fetchVerticalEmbedItemsFromDevContext ?? undefined;

export const useDevVerticalEmbedItem = () =>
  React.useContext(DevContext)?.fetchVerticalEmbedItemFromDevContext ?? undefined;
